/* ─── UserBar — messages, notifications, avatar dropdown for right rail ─── */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Award, Edit, Settings, LogOut, LogIn } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import NotificationBell from './NotificationBell.tsx';
import TrialCountdownChip from './remember/TrialCountdownChip';
import { trialCountdownOf } from '../services/trialService';

export default function UserBar({ user }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const unreadMessages = useUnreadMessages(user?.uid);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);

  // Close on route change
  useEffect(() => {
    setShowUserMenu(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNav = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  if (!user) {
    return (
      <div className="right-rail-user-bar">
        <Link to="/login" className="right-rail-login-btn">
          <LogIn size={16} />
          <span>login</span>
        </Link>
      </div>
    );
  }

  const isPremium = user.selectedPlan === 'premium';
  const trialCountdown = trialCountdownOf(user);

  return (
    <div className="right-rail-user-bar">
      <div className="right-rail-spacer"></div>

      {/* Go Premium CTA — the countdown replaces it while trialing (PPL-358). */}
      {!isPremium && (trialCountdown.isTrialing ? (
        <TrialCountdownChip countdown={trialCountdown} />
      ) : (
        <Link to="/premium" className="top-bar-premium-cta" aria-label="Go Premium">
          <Award size={14} />
          <span>go premium</span>
        </Link>
      ))}

      {/* Messages */}
      <Link to="/messages" className="top-bar-icon-btn" aria-label="Messages">
        <span className="nav-icon-wrapper">
          <Mail size={18} />
          {unreadMessages > 0 && (
            <span className="nav-badge">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
          )}
        </span>
      </Link>

      {/* Notifications */}
      <NotificationBell />

      {/* User avatar + dropdown */}
      <div className="top-bar-user" ref={userMenuRef}>
        <button className="top-bar-avatar-btn" onClick={() => setShowUserMenu(!showUserMenu)} aria-label="User menu">
          {user.photoURL ? (
            <img src={user.photoURL} alt="" className="top-bar-avatar" />
          ) : (
            <div className="top-bar-avatar-placeholder">
              {(user.userHandle || user.displayName || user.email || '?')[0].toUpperCase()}
            </div>
          )}
        </button>

        {showUserMenu && (
          <div className="user-dropdown">
            <div className="user-dropdown-header">
              <span className="user-dropdown-name">
                @{user.userHandle || user.tetherHandle || user.displayName || user.email?.split('@')[0]}
              </span>
            </div>
            <button className="user-dropdown-item" onClick={() => handleNav('/premium')}>
              <Award size={16} />
              <span>{user.selectedPlan === 'premium' ? 'premium' : 'go premium'}</span>
            </button>
            <button className="user-dropdown-item" onClick={() => handleNav('/profile/edit')}>
              <Edit size={16} />
              <span>edit profile</span>
            </button>
            <button className="user-dropdown-item" onClick={() => handleNav('/settings')}>
              <Settings size={16} />
              <span>settings</span>
            </button>
            <div className="user-dropdown-divider"></div>
            <button className="user-dropdown-item logout" onClick={handleLogout}>
              <LogOut size={16} />
              <span>logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
